import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../api/client";
import * as settingsApi from "../../../api/settings";
import { SystemSettingItem } from "../../../api/settings";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Badge } from "../../../components/ui/Badge";

// `test`: la categoria ha un servizio esterno da provare. "ritiri" no: sono
// regole del modulo Ritiri valori (approvazione, destinatari delle bozze,
// invio dall'app acceso o spento), e l'invio passa dalla connessione della
// categoria "email", che ha già la sua prova.
const CATEGORIE: { chiave: "email" | "odoo" | "sms" | "ritiri"; titolo: string; test: boolean }[] = [
  { chiave: "email", titolo: "Email (Microsoft Graph)", test: true },
  { chiave: "odoo", titolo: "Odoo", test: true },
  { chiave: "sms", titolo: "SMS (Twilio)", test: true },
  { chiave: "ritiri", titolo: "Ritiri valori", test: false },
];

export default function SystemSettings() {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<SystemSettingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [testingCategoria, setTestingCategoria] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; error?: string }>>({});

  /**
   * Gli errori di QUESTA pagina non scompaiono da soli: si chiudono con la X.
   * Sono messaggi di configurazione — spesso citano il valore rifiutato, il
   * motivo e come correggerlo — e tre secondi non bastano per leggerli, tanto
   * meno per annotarli. Le conferme positive restano invece a scomparsa breve:
   * "Impostazione salvata" non ha nulla da leggere con calma.
   */
  const showError = (message: string) => showToast(message, "error", { persistent: true });

  async function load() {
    setIsLoading(true);
    try {
      const { settings: list } = await settingsApi.listSettings();
      setSettings(list);
    } catch (err) {
      showError(getErrorMessage(err, "Impossibile caricare le impostazioni."));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave(chiave: string) {
    const valore = drafts[chiave];
    if (!valore) return;

    setSavingKey(chiave);
    try {
      await settingsApi.updateSetting(chiave, valore);
      showToast("Impostazione salvata.", "success");
      setDrafts((prev) => ({ ...prev, [chiave]: "" }));
      load();
    } catch (err) {
      showError(getErrorMessage(err, "Impossibile salvare."));
    } finally {
      setSavingKey(null);
    }
  }

  async function handleReset(chiave: string) {
    const confirmed = window.confirm("Tornare a usare il valore da .env per questo campo?");
    if (!confirmed) return;

    setSavingKey(chiave);
    try {
      await settingsApi.resetSettingToEnv(chiave);
      showToast("Ripristinato il valore da .env.", "success");
      load();
    } catch (err) {
      showError(getErrorMessage(err, "Impossibile ripristinare."));
    } finally {
      setSavingKey(null);
    }
  }

  async function handleTest(categoria: "email" | "odoo" | "sms") {
    setTestingCategoria(categoria);
    try {
      const result = await settingsApi.testConnection(categoria);
      setTestResults((prev) => ({ ...prev, [categoria]: result }));
      // Il test è proprio il momento in cui l'errore del provider va letto per
      // intero: è la diagnosi, non una notifica di cortesia.
      if (result.success) showToast("Connessione riuscita.", "success");
      else showError(`Connessione fallita: ${result.error}`);
    } catch (err) {
      showError(getErrorMessage(err, "Impossibile testare la connessione."));
    } finally {
      setTestingCategoria(null);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Impostazioni di sistema</h1>
        <p className="mt-1 text-sm text-ink/50">
          Credenziali per email, Odoo e SMS, e regole delle bozze dei Ritiri valori. I valori riservati sono
          cifrati nel database e non vengono mai mostrati di nuovo in chiaro — solo sovrascritti.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : (
        <div className="flex flex-col gap-6">
          {CATEGORIE.map((cat) => {
            const items = settings.filter((s) => s.categoria === cat.chiave);
            const testResult = testResults[cat.chiave];

            return (
              <div key={cat.chiave} className="rounded-card border border-line bg-white p-5 shadow-card">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-base font-bold text-ink">{cat.titolo}</h2>
                  <div className="flex items-center gap-2">
                    {testResult && (
                      <span className="flex items-center gap-1 text-xs">
                        {testResult.success ? (
                          <CheckCircle2 className="h-4 w-4 text-[#22c55e]" aria-hidden="true" />
                        ) : (
                          <XCircle className="h-4 w-4 text-accent" aria-hidden="true" />
                        )}
                        {testResult.success ? "Connessione ok" : testResult.error}
                      </span>
                    )}
                    {cat.test && cat.chiave !== "ritiri" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={testingCategoria === cat.chiave}
                        onClick={() => handleTest(cat.chiave as "email" | "odoo" | "sms")}
                      >
                        Testa connessione
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  {items.map((item) => (
                    <div
                      key={item.chiave}
                      className="flex items-end gap-2 border-t border-line pt-3 first:border-0 first:pt-0"
                    >
                      <div className="flex-1">
                        <Input
                          label={item.descrizione}
                          type={item.is_secret ? "password" : "text"}
                          placeholder={
                            item.is_secret
                              ? item.is_set_in_db
                                ? "•••••••• (impostato — inserisci un nuovo valore per sostituirlo)"
                                : item.is_set_in_env
                                  ? "Usa il valore da .env — inserisci un valore per sovrascriverlo"
                                  : "Non configurato"
                              : (item.valore ?? (item.is_set_in_env ? "Usa il valore da .env" : "Non configurato"))
                          }
                          value={drafts[item.chiave] ?? ""}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [item.chiave]: e.target.value }))}
                        />
                      </div>

                      <Badge color={item.is_set_in_db ? "success" : item.is_set_in_env ? "primary" : "neutral"}>
                        {item.is_set_in_db ? "Da database" : item.is_set_in_env ? "Da .env" : "Non impostato"}
                      </Badge>

                      <Button
                        size="sm"
                        loading={savingKey === item.chiave}
                        disabled={!drafts[item.chiave]}
                        onClick={() => handleSave(item.chiave)}
                      >
                        Salva
                      </Button>

                      {item.is_set_in_db && (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={savingKey === item.chiave}
                          onClick={() => handleReset(item.chiave)}
                        >
                          {cat.chiave === "ritiri" ? "Svuota" : "Ripristina .env"}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
